// portal-admin.js — Phase 3.11: Portal Admin View (Ryan's side)
// Active portal accounts · Unread director messages · Provision new access

import { apiFetch, formatDate } from './utils.js';

export function initPortalAdmin() {
  window.renderPortalAdminView = renderPortalAdminView;
}

export async function renderPortalAdminView() {
  const container = document.getElementById('view-portal-admin');
  if (!container) return;

  container.innerHTML = '<div class="loading-state">Loading portal data…</div>';

  const [accounts, pending] = await Promise.all([
    apiFetch('/api/portal/accounts').catch(() => []),
    apiFetch('/api/portal/messages/pending').catch(() => [])
  ]);

  container.innerHTML = `
    <div class="portal-admin-view">
      <div class="portal-admin-header">
        <h2>Director Portal</h2>
        <button class="btn-provision" onclick="window.openProvisionModal()">+ Provision Access</button>
      </div>

      ${pending.length > 0 ? `
        <div class="portal-pending-section">
          <div class="section-label">Unread Messages <span class="pending-count">${pending.length}</span></div>
          ${pending.map(msg => `
            <div class="portal-pending-msg" data-client="${msg.client_id}">
              <div class="pending-from">${msg.client_name}</div>
              <div class="pending-body">${msg.body}</div>
              <div class="pending-meta">${formatDate(msg.created_at)}</div>
              <button class="btn-reply-msg" onclick="window.openReplyModal(${msg.client_id}, '${escAttr(msg.client_name)}')">Reply</button>
            </div>
          `).join('')}
        </div>
      ` : ''}

      <div class="portal-accounts-section">
        <div class="section-label">Active Portals <span class="accounts-count">${accounts.length}</span></div>
        ${accounts.length === 0
          ? '<div class="empty-state">No active portal accounts. Provision access for a director to get started.</div>'
          : accounts.map(acc => `
              <div class="portal-account-row">
                <div class="account-info">
                  <span class="account-name">${acc.client_name}</span>
                  <span class="account-email">${acc.email}</span>
                  ${acc.show_title ? `<span class="account-show">${acc.show_title}${acc.season_year ? ' · ' + acc.season_year : ''}</span>` : ''}
                </div>
                <div class="account-meta">
                  <span class="account-last-used">${acc.token_last_used_at ? 'Last visit: ' + formatDate(acc.token_last_used_at) : 'Never visited'}</span>
                  <button class="btn-reissue" onclick="window.reissuePortalToken(${acc.client_id}, ${acc.show_id || null})">Reissue Link</button>
                </div>
              </div>
            `).join('')}
      </div>
    </div>

    <!-- Provision modal -->
    <div id="provision-modal" class="modal-overlay hidden">
      <div class="modal-dialog">
        <div class="modal-header">
          <span>Provision Portal Access</span>
          <button class="modal-close-btn" onclick="document.getElementById('provision-modal').classList.add('hidden')">×</button>
        </div>
        <div class="modal-body">
          <div class="form-field" style="position:relative">
            <label>Client Name</label>
            <input type="text" id="prov-client-search" class="form-input" placeholder="Start typing a name…" autocomplete="off">
            <div id="prov-client-suggestions" style="display:none;position:absolute;top:100%;left:0;right:0;background:#111d2e;border:1px solid rgba(0,180,216,0.2);border-radius:4px;z-index:50;max-height:180px;overflow-y:auto"></div>
            <input type="hidden" id="prov-client-id">
          </div>
          <div class="form-field">
            <label>Show (optional)</label>
            <select id="prov-show-id" class="form-input">
              <option value="">— Any / Most Recent —</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button onclick="window.provisionPortalAccess()" class="btn-primary">Generate Link</button>
        </div>
      </div>
    </div>

    <!-- Reply modal -->
    <div id="reply-modal" class="modal-overlay hidden">
      <div class="modal-dialog">
        <div class="modal-header">
          <span>Reply to <span id="reply-director-name"></span></span>
          <button class="modal-close-btn" onclick="document.getElementById('reply-modal').classList.add('hidden')">×</button>
        </div>
        <div class="modal-body">
          <input type="hidden" id="reply-client-id">
          <div class="form-field">
            <label>Message</label>
            <textarea id="reply-body" class="form-input" rows="4" placeholder="Your reply…"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button onclick="window.sendPortalReply()" class="btn-primary">Send Reply</button>
        </div>
      </div>
    </div>
  `;
}

function escAttr(str) {
  return String(str || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// ── Window handlers ────────────────────────────────────────────────────────

window.openProvisionModal = function() {
  // Clear previous state
  const search = document.getElementById('prov-client-search');
  const hiddenId = document.getElementById('prov-client-id');
  const showSel = document.getElementById('prov-show-id');
  if (search) search.value = '';
  if (hiddenId) hiddenId.value = '';
  if (showSel) { showSel.innerHTML = '<option value="">— Any / Most Recent —</option>'; }
  document.getElementById('provision-modal')?.classList.remove('hidden');

  // Wire up client search autocomplete
  if (search && !search.dataset.wired) {
    search.dataset.wired = '1';
    search.addEventListener('input', async () => {
      const q = search.value.trim();
      const box = document.getElementById('prov-client-suggestions');
      if (!q || q.length < 2) { box.style.display = 'none'; return; }
      try {
        const clients = await apiFetch(`/api/v1/agent/clients?q=${encodeURIComponent(q)}`);
        if (!clients.length) { box.style.display = 'none'; return; }
        box.innerHTML = clients.slice(0, 8).map(c => `
          <div data-id="${c.id}" data-name="${escAttr(c.org_name || c.name)}"
               style="padding:8px 10px;cursor:pointer;font-size:0.82rem;color:#e8f4fd;border-bottom:1px solid rgba(255,255,255,0.05)"
               onmouseover="this.style.background='rgba(0,180,216,0.08)'"
               onmouseout="this.style.background=''">
            ${escAttr(c.org_name || c.name)}
            ${c.name && c.org_name ? `<span style="color:#5a7a9a;font-size:0.75rem"> · ${escAttr(c.name)}</span>` : ''}
          </div>
        `).join('');
        box.style.display = 'block';
        box.querySelectorAll('[data-id]').forEach(row => {
          row.addEventListener('click', async () => {
            const id = row.dataset.id;
            const name = row.dataset.name;
            search.value = name;
            hiddenId.value = id;
            box.style.display = 'none';
            // Load shows for this client
            try {
              const shows = await apiFetch(`/api/v1/clients/${id}/shows`);
              showSel.innerHTML = '<option value="">— Any / Most Recent —</option>' +
                shows.map(s => `<option value="${s.id}">${s.season_year}${s.title ? ' · ' + s.title : ''}</option>`).join('');
            } catch (_) {}
          });
        });
      } catch (_) { box.style.display = 'none'; }
    });
    document.addEventListener('click', e => {
      if (!search.contains(e.target)) {
        document.getElementById('prov-client-suggestions').style.display = 'none';
      }
    });
  }
};

window.provisionPortalAccess = async function() {
  const clientId = document.getElementById('prov-client-id')?.value;
  const showId   = document.getElementById('prov-show-id')?.value;

  if (!clientId) { alert('Select a client first'); return; }

  try {
    const result = await apiFetch('/api/portal/provision', {
      method: 'POST',
      body: JSON.stringify({ client_id: parseInt(clientId), show_id: showId ? parseInt(showId) : null })
    });
    document.getElementById('provision-modal')?.classList.add('hidden');
    alert(`Portal link generated:\n\n${result.portal_url}\n\nSend this to ${result.email}`);
    renderPortalAdminView();
  } catch (err) {
    alert('Failed to provision access: ' + err.message);
  }
};

window.reissuePortalToken = async function(clientId, showId) {
  if (!confirm('Reissue portal link? The old link will stop working.')) return;
  try {
    const result = await apiFetch('/api/portal/reissue', {
      method: 'POST',
      body: JSON.stringify({ client_id: clientId })
    });
    alert(`New portal link:\n\n${result.portal_url}`);
    renderPortalAdminView();
  } catch (err) {
    alert('Failed to reissue link: ' + err.message);
  }
};

window.openReplyModal = function(clientId, clientName) {
  document.getElementById('reply-client-id').value = clientId;
  document.getElementById('reply-director-name').textContent = clientName;
  document.getElementById('reply-body').value = '';
  document.getElementById('reply-modal')?.classList.remove('hidden');
};

window.sendPortalReply = async function() {
  const clientId = document.getElementById('reply-client-id')?.value;
  const body     = document.getElementById('reply-body')?.value?.trim();
  if (!body) { alert('Message is required'); return; }

  try {
    await apiFetch('/api/portal/messages/reply', {
      method: 'POST',
      body: JSON.stringify({ client_id: parseInt(clientId), body })
    });
    document.getElementById('reply-modal')?.classList.add('hidden');
    renderPortalAdminView();
  } catch (err) {
    alert('Failed to send reply: ' + err.message);
  }
};
